import { useState } from "react";
import { AppShell } from "@/components/layout/app-shell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Plus, Building2, ArrowRight } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useCustomer } from "@/hooks/use-customer";
import { useLocation } from "wouter";
import { queryClient } from "@/lib/queryClient";

export default function CustomerAccessPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { customers, isLoading, setActiveCustomerId } = useCustomer();
  const [facilityName, setFacilityName] = useState("");

  const createFacilityMutation = useMutation({
    mutationFn: async (data: { username: string }) => {
      const res = await apiRequest("POST", "/api/admin/customers", data);
      return await res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/customers"] });
      setFacilityName("");
      toast({
        title: "Facility Created",
        description: `Facility "${data.username}" has been created successfully.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create facility",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!facilityName.trim()) {
      toast({
        title: "Validation Error",
        description: "Facility name cannot be empty.",
        variant: "destructive"
      });
      return;
    }
    createFacilityMutation.mutate({ username: facilityName.trim() });
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-primary">Facilities</h1>
          <p className="text-muted-foreground mt-2">
            Create and select a facility context to group forms, analyses, and onboarding proposals under a single building.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2">
            <form onSubmit={handleSubmit}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Plus className="h-5 w-5" />
                  Add Facility
                </CardTitle>
                <CardDescription>
                  Enter the facility or business name to create a new context.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="facilityName">Facility / Business Name</Label>
                  <Input
                    id="facilityName"
                    placeholder="e.g. Russo Office Plaza (Test 4)"
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  type="submit" 
                  disabled={createFacilityMutation.isPending}
                  className="w-full"
                >
                  {createFacilityMutation.isPending ? "Creating..." : "Create Facility"}
                </Button>
              </CardFooter>
            </form>
          </Card>

          <Card className="bg-muted/10">
            <CardHeader>
              <CardTitle className="text-lg">Instructions</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                Facilities act as a namespace container for all operational data in OOE (Energy Intelligence).
              </p>
              <p>
                Once a facility is created, click <strong>"Work as..."</strong> in the list below to set the active context. This enables sub-headers like Equipment Capture, Saved Analyses, and NYSERDA forms.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Existing Customers List */}
        <div className="mt-12 pt-8 border-t">
          <h2 className="text-2xl font-bold tracking-tight mb-6">Existing Facilities / Businesses</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              <p className="text-muted-foreground col-span-full">Loading facilities...</p>
            ) : customers?.length === 0 ? (
              <div className="col-span-full text-center py-12 border rounded-lg bg-muted/20">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Facilities Found</h3>
                <p className="text-muted-foreground">You have not created any facility accounts yet.</p>
              </div>
            ) : (
              customers?.map((customer) => (
                <Card 
                  key={customer.id} 
                  className="hover:border-primary/50 transition-colors cursor-pointer flex flex-col"
                  onClick={() => {
                    setActiveCustomerId(customer.id);
                    setLocation("/");
                  }}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      {customer.username}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-sm text-muted-foreground">Select this facility to manage its NYSERDA forms, OsEM entries, and Pump Analyses.</p>
                  </CardContent>
                  <div className="p-6 pt-0 mt-auto">
                    <Button variant="outline" className="w-full gap-2">
                      Work as {customer.username} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
